import type { v2, ControlTypeFunction, DeprecatedProperties } from '@sap/ux-specification-types';
import { ControlType, PropertyName, QUICKVARPATH, QUICKVARPATHX } from '@sap/ux-specification-types';
import { addPatternForBindingChangeOfEnumLR, convertEnumToCSV } from '../../../common';
import { buildControlIdFromParent, childId, syncRule, validity, descriptionSrcURL } from '../../../common/decoration';
import { getAppSettingPathV2, getDataLoadSettingPathV2, getTableSettingPathV2 } from '../../application';
import type { PopinLayout } from 'sap/m/library';
import type { smarttable } from 'sap/ui/comp/library';

const MULTIEDITPATH = '/multiEdit';

export const getSmartTableControlId = (
    baseId: string,
    idBreadcrumbs: string[],
    _breadcrumbs: string[],
    controlType: ControlTypeFunction
): string => {
    if (controlType() === 'sap.m.Table') {
        return baseId + 'responsiveTable';
    } else if (controlType() === 'sap.ui.comp.smarttable.SmartTable' && baseId?.includes('AnalyticalListPage')) {
        return baseId + PropertyName.table;
    }
    return buildControlIdFromParent(baseId, idBreadcrumbs);
};

const syncRuleForSmartTableFlexProperty = {
    flex: {
        controlId: getSmartTableControlId,
        controlType: (): ControlType => ControlType.SmartTable
    }
};

const syncRuleForResponsiveTableFlexProperty = {
    flex: {
        controlId: getSmartTableControlId, // Note that this function evaluates the control type and returns the id of the inner table if applicable
        controlType: (): ControlType => ControlType.Table
    }
};

/**
 * Import createWithParameterDialog (only) fields to Array: { field: { path: field } } => fields[].
 *
 * @param manifestSection - The section of the manifest to import create parameters from
 * @returns {{ fields: string[] }} - An object containing an array of field names.
 */
export function importCreateParameters(manifestSection: object): object {
    if (!manifestSection['createWithParameterDialog']?.fields) {
        return undefined;
    }
    const manifestDialogFields = JSON.parse(JSON.stringify(manifestSection['createWithParameterDialog'].fields));
    const configPart = { fields: [] } as Fields4Dialog;
    for (const manifestField in manifestDialogFields) {
        configPart.fields.push(manifestField);
    }
    return configPart;
}

/**
 * Export createWithParameterDialog fields to manifest.
 *
 * @param manifestSection - The section of the manifest to export parameters to
 * @param configPart - The configuration object containing fields to export
 * @param key - The key representing the fields in the configuration object
 */
export function exportCreateParametersFields(manifestSection: unknown, configPart: Fields4Dialog, key: string): void {
    const fields: string[] = configPart[key];
    if (fields.length === 0) {
        delete manifestSection[key];
        return;
    }
    const manifestFields = {};
    if (!manifestSection[key]) {
        manifestSection[key] = {};
    }
    fields.forEach((entry) => (manifestFields[entry] = manifestSection[key][entry] || { path: entry }));
    manifestSection[key] = manifestFields;
}

/**
 * Export variants to manifest.
 *
 * @param manifestSection - The section of the manifest to process
 * @param configObject - The configuration object containing relevant settings
 */
function exportVariants(manifestSection: object, configObject: object): void {
    const manifestVariants = manifestSection['variants'] || {};
    const configVariants = configObject['variants'] || {};
    // Search for variants missing in config
    const deletableVariants = Object.keys(manifestVariants).filter(
        (variant) => !Object.keys(configVariants).includes(variant)
    );
    // Delete missing variants from manifest
    deletableVariants.forEach((variant) => {
        delete manifestVariants[variant];
    });
    for (const variant in configVariants) {
        const variantConfig = configVariants[variant];
        const tablesettings: string[] = ['type', 'multiSelect', 'selectAll', 'selectionLimit', 'inlineDelete'];
        for (const key in variantConfig) {
            if (tablesettings.indexOf(key) !== -1) {
                if (!variantConfig['tableSettings']) {
                    variantConfig['tableSettings'] = {};
                }
                variantConfig['tableSettings'][key] = variantConfig[key];
                delete variantConfig[key];
            }
        }
        manifestVariants[variant] = variantConfig;
    }
    if (Object.keys(manifestVariants).length > 0) {
        manifestSection['variants'] = manifestVariants;
    }
}

/**
 * Export quick variant selection X to manifest.
 *
 * @param manifestSection - The section of the manifest to process
 * @param configObject - The configuration object containing relevant settings
 */
function exportQuickVariantSelectionX(manifestSection: object, configObject: object): void {
    if (!configObject['variants']) {
        return;
    }
    if (Object.keys(configObject['variants']).length > 0) {
        exportVariants(manifestSection, configObject);
    } else {
        delete manifestSection['variants'];
    }
}

/**
 * Imports multi-edit settings from the manifest section.
 *
 * @param manifestSection - The section of the manifest containing multi-edit settings.
 * @returns The multi-edit configuration object or undefined if not present.
 */
function importMultiEdit(manifestSection) {
    if (!manifestSection || !manifestSection['multiEdit']) {
        return undefined;
    }
    const configPart: v2.MultiEdit = JSON.parse(JSON.stringify(manifestSection['multiEdit']));

    // convert CSV format to array of strings
    configPart.ignoredFields = manifestSection['multiEdit']?.['ignoredFields']?.split(',');

    return configPart;
}

export class CreateWithFilters {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys) + '/createWithFilters'
        }
    })
    strategy: v2.Strategy;
}

export class MultiEdit implements v2.MultiEdit {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getTableSettingPathV2(pageKeys) + MULTIEDITPATH
        }
    })
    enabled?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getTableSettingPathV2(pageKeys) + MULTIEDITPATH
        }
    })
    annotationPath?: string;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getTableSettingPathV2(pageKeys) + MULTIEDITPATH,
            export: convertEnumToCSV
        }
    })
    ignoredFields?: v2.IgnoredFieldsType[];
}

/**
 * Imports the type of the table from the manifest section.
 *
 * @param manifestSection - The section of the manifest containing table settings.
 * @returns The type of the table as a string, or undefined if not present.
 */
function importType(manifestSection: object): string {
    if (manifestSection?.['tableSettings']?.['type']) {
        return manifestSection['tableSettings']['type'];
    } else if (manifestSection?.['tableType']) {
        return manifestSection['tableType'];
    }
}

/**
 * Export table type.
 *
 * @param manifestSection - The section of the manifest to process
 * @param configPart - The configuration object containing fields to export
 */
export function exportType(manifestSection: unknown, configPart: object): void {
    if (configPart?.['type']) {
        if (!manifestSection['tableSettings']) {
            manifestSection['tableSettings'] = {};
        }
        manifestSection['tableSettings']['type'] = configPart['type'];
        if (manifestSection?.['tableType']) {
            //old format
            delete manifestSection['tableType'];
        }
    }
}

/**
 * Delete table type.
 *
 * @param manifestSection - The section of the manifest to process
 */
export function deleteType(manifestSection: unknown): void {
    if (manifestSection?.['tableSettings']?.['type']) {
        delete manifestSection['tableSettings']['type'];
    }
    if (manifestSection?.['tableType']) {
        //old format
        delete manifestSection['tableType'];
    }
}

export class DataLoadSettings {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getDataLoadSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.75.2'
    })
    loadDataOnAppLaunch: v2.LoadDataOnAppLaunchSettings;
}

export class Fields4Dialog implements v2.Fields4Dialog {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getTableSettingPathV2(pageKeys) + '/createWithParameterDialog',
            export: exportCreateParametersFields
        }
    })
    @validity({
        since: '1.77.0'
    })
    fields: string[];
}

export class QuickVariantSelection {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys) + QUICKVARPATH
        }
    })
    @validity({
        since: '1.48.0'
    })
    showCounts?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys) + QUICKVARPATH
        }
    })
    @validity({
        since: '1.48.0'
    })
    variants: {
        [key: number]: v2.QuickVariant;
    };
}

export class QuickVariantSelectionX implements v2.QuickVariantSelectionX {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys) + QUICKVARPATHX
        }
    })
    @validity({
        since: '1.48.0'
    })
    showCounts?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys) + QUICKVARPATHX
        }
    })
    @validity({
        since: '1.48.0'
    })
    enableAutoBinding?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys) + QUICKVARPATHX,
            export: exportQuickVariantSelectionX
        }
    })
    @validity({
        since: '1.48.0'
    })
    variants: {
        [key: number]: v2.QuickVariantX;
    };
}

export class CommonTableSettings<COLS = v2.GenericColumns> implements v2.CommonTableSettings<COLS> {
    getDeprecated = (): DeprecatedProperties => {
        return {
            tableType: {
                since: '1.50.0',
                deprecated: {
                    since: '1.70.0',
                    text: 'Use tableSettings.type instead'
                }
            },
            enableAutoBinding: {
                since: '1.40.0',
                deprecated: {
                    since: '1.75.2',
                    text: 'Use tableSettings.loadDataOnAppLaunch instead'
                }
            }
        };
    };

    @syncRule({
        manifest: {
            key: 'createWithParameterDialog',
            path: (pageKeys): string => getTableSettingPathV2(pageKeys),
            import: importCreateParameters
        }
    })
    @validity({
        since: '1.77.0'
    })
    createWithParameterDialog?: Fields4Dialog;

    @syncRule({
        flex: {
            controlId: buildControlIdFromParent,
            controlType: (): ControlType => ControlType.SmartTable
        }
    })
    @validity({
        since: '1.38.0'
    })
    showTablePersonalisation?: boolean;

    @syncRule(syncRuleForSmartTableFlexProperty)
    @validity({
        since: '1.145.0'
    })
    enableExport?: boolean;

    @syncRule(syncRuleForSmartTableFlexProperty)
    @validity({
        since: '1.26.0'
    })
    useExportToExcel?: boolean;

    @syncRule(syncRuleForSmartTableFlexProperty)
    @validity({
        since: '1.50.0'
    })
    exportType?: smarttable.ExportType;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.48.0'
    })
    quickVariantSelection?: QuickVariantSelection;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.48.0'
    })
    quickVariantSelectionX?: QuickVariantSelectionX;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getTableSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.38.0'
    })
    multiSelect?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getTableSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.120.0'
    })
    copy?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getTableSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.70.0'
    })
    selectAll?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getTableSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.70.0'
    })
    selectionLimit?: number;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.75.2'
    })
    dataLoadSettings?: DataLoadSettings;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.42.0'
    })
    condensedTableLayout?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.56.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/3e2b4d212b66481a829ccef1dc0ca16b')
    enableTableFilterInPageVariant?: boolean;

    @childId('toolBar')
    toolBar?: v2.ToolBar;
}

export class ResponsiveTable extends CommonTableSettings implements v2.ResponsiveTable {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys),
            import: importType,
            export: exportType,
            delete: deleteType
        }
    })
    @validity({
        since: '1.50.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    type?: v2.TableTypeV2.ResponsiveTable;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.54.0'
    })
    createWithFilters?: CreateWithFilters;

    @syncRule({
        flex: syncRuleForResponsiveTableFlexProperty.flex,
        generate: addPatternForBindingChangeOfEnumLR
    })
    @validity({
        since: '1.52.0'
    })
    popinLayout?: PopinLayout;

    @syncRule(syncRuleForResponsiveTableFlexProperty)
    includeItemInSelection?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getTableSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.77.0'
    })
    inlineDelete?: boolean;

    @syncRule(syncRuleForResponsiveTableFlexProperty)
    @validity({
        since: '1.16.0'
    })
    growingThreshold?: number;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getTableSettingPathV2(pageKeys),
            import: importMultiEdit
        }
    })
    @validity({
        since: '1.98.0'
    })
    multiEdit?: MultiEdit;
}

export class TreeTable extends CommonTableSettings implements v2.TreeTable {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys),
            import: importType,
            export: exportType,
            delete: deleteType
        }
    })
    @validity({
        since: '1.50.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    type?: v2.TableTypeV2.TreeTable;
}

export class AnalyticalTable extends CommonTableSettings implements v2.AnalyticalTable {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys),
            import: importType,
            export: exportType,
            delete: deleteType
        }
    })
    @validity({
        since: '1.50.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    type?: v2.TableTypeV2.AnalyticalTable;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    qualifier?: string;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.38.0'
    })
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    defaultContentView?: v2.DefaultContentView;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    defaultFilterMode?: v2.DefaultFilterMode;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.60.0'
    })
    contentTitle?: string;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    autoHide?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.78.0'
    })
    allFiltersAsInParameters?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.54.0'
    })
    createWithFilters?: CreateWithFilters;
}

export class TableALP extends CommonTableSettings implements v2.TableALP {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys),
            import: importType,
            export: exportType,
            delete: deleteType
        }
    })
    @validity({
        since: '1.50.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    type?: v2.TableTypeV2.AnalyticalTable & v2.TableTypeV2.ResponsiveTable & v2.TableTypeV2.GridTable;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    qualifier?: string;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.38.0'
    })
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    defaultContentView?: v2.DefaultContentView;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    defaultFilterMode?: v2.DefaultFilterMode;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.60.0'
    })
    contentTitle?: string;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    autoHide?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.78.0'
    })
    allFiltersAsInParameters?: boolean;
}

export class ALPResponsiveTable extends TableALP {
    /*No sync rule needed for type as it is already part of TableALP. This also caused sync issues with flexChanges in release 1.60 as tabletype is not explicity set to ResponsiveTable in 1.60
    During import, Table type is used in function "addFlex" to refer to correct definition in schema.
    */
    @syncRule(syncRuleForResponsiveTableFlexProperty)
    @validity({
        since: '1.16.0'
    })
    growingThreshold?: number;
}

export class ALPGridTable extends TableALP {}

export class ALPAnalyticalTable extends TableALP {}

export class GridTable extends CommonTableSettings implements v2.GridTable {
    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys),
            import: importType,
            export: exportType,
            delete: deleteType
        }
    })
    @validity({
        since: '1.50.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    type?: v2.TableTypeV2.GridTable;

    @syncRule({
        manifest: {
            path: (pageKeys): string => getAppSettingPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.54.0'
    })
    createWithFilters?: CreateWithFilters;
}
